import { UserGroup } from "../domain/UserGroup";

// Constants for group type classification
export const GROUP_TYPE_KEYWORDS = {
    ADMIN: "admin",
    USER: "user"
} as const;

export type GroupType = "admin" | "user";

export interface GroupClassificationResult {
    userGroup: UserGroup | null;
    adminGroup: UserGroup | null;
    hasValidGroups: boolean;
}

export const GroupClassifier = {
    /**
     * Determines if a group is an admin-level group. NOTE: This only checks the string "admin" exists so it's fragile
     */
    isAdmin: (group: Partial<UserGroup> | null): boolean => {
        return group?.name?.toLowerCase()?.includes(GROUP_TYPE_KEYWORDS.ADMIN) ?? false;
    },

    /**
     * Determines if a group is a user-level group. NOTE: This only checks the string "user" exists so it's fragile
     */
    isUser: (group: Partial<UserGroup> | null): boolean => {
        return group?.name?.toLowerCase()?.includes(GROUP_TYPE_KEYWORDS.USER) ?? false;
    },

    /**
     * Finds the first group of a specific type from an array of groups
     */
    findByType: (groups: UserGroup[], type: GroupType): UserGroup | null => {
        const classifier = type === GROUP_TYPE_KEYWORDS.ADMIN ? GroupClassifier.isAdmin : GroupClassifier.isUser;
        return groups.find((group) => classifier(group)) || null;
    },

    /**
     * Classifies available groups into user and admin groups
     */
    classifyGroups: (groups: UserGroup[]): GroupClassificationResult => {
        const userGroup = GroupClassifier.findByType(groups, "user");
        const adminGroup = GroupClassifier.findByType(groups, "admin");

        return {
            userGroup,
            adminGroup,
            hasValidGroups: Boolean(userGroup && adminGroup)
        };
    },

    /**
     * Instance-aware classification that ensures the group actually has permissions for the given instance
     */
    classifyGroupsForInstance: (groups: UserGroup[], instanceId?: string): GroupClassificationResult => {
        if (!instanceId) {
            return { userGroup: null, adminGroup: null, hasValidGroups: false };
        }
        const hasInstanceRoles = (g: UserGroup) =>
            (g.instancePermissions || []).some((ip) => ip.instanceId === instanceId && (ip.roleIds || []).length > 0);
        const userGroup = groups.find((g) => GroupClassifier.isUser(g) && hasInstanceRoles(g)) || null;
        const adminGroup = groups.find((g) => GroupClassifier.isAdmin(g) && hasInstanceRoles(g)) || null;
        return {
            userGroup,
            adminGroup,
            hasValidGroups: Boolean(userGroup && adminGroup)
        };
    },

    /**
     * Get all roleIds for a group scoped optionally to an instanceId
     */
    getRoleIdsForGroupInstance: (group: UserGroup | null, instanceId?: string): string[] => {
        if (!group) return [];
        return (group.instancePermissions || [])
            .filter((ip) => !instanceId || ip.instanceId === instanceId)
            .flatMap((ip) => ip.roleIds || []);
    },

    /**
     * Find the group for a given role id (optionally restricting to a specific instance)
     */
    findGroupForRole: ({
        groups,
        roleId,
        instanceId
    }: {
        groups: UserGroup[];
        roleId: string;
        instanceId?: string;
    }): UserGroup | null => {
        if (!roleId) return null;
        return (
            groups.find((g) =>
                (g.instancePermissions || []).some(
                    (ip) => (!instanceId || ip.instanceId === instanceId) && (ip.roleIds || []).includes(roleId)
                )
            ) || null
        );
    }
};
